import React, { useEffect, useRef, useState } from "react";
import "./MainContent.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Circles } from "react-loader-spinner";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import TabContext from "@mui/lab/TabContext";
import TabPanel from "@mui/lab/TabPanel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";

import Grid from "@mui/material/Grid2";
import Tooltip from "@mui/material/Tooltip";
import ResponseRenderer from "./ResponseRenderer";
import _ from "lodash";
import Snackbar from "@mui/material/Snackbar";
import TestResults from "./TestResults";
import CookiesPanel from "./CookiesPanel";
import { useVariables } from "../../../context/VariableContext";
import VariableInput from "../Variables/VariableInput";
import { asMap, createScopeResolver, interpolateText } from "../utils/variableInterpolation";

import TestCaseModal from "./TestCaseModal";
import ChainTestResults from "./ChainTestResults";
import Chain from "./Chain";
import Collection from "./Collection";
import CollectionResults from "./CollectionResults";
import EndpointForm from "./EndpointForm";
import CollectionForm from "./CollectionForm";
import TestCaseForm from "./TestCaseForm";
import ChainCreationModal from "./ChainCreationModal";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import HistoryPanel from "./HistoryPanel";
import TopNav from "./TopNav";
import ConstructUrl from "./ConstructUrl";
import TestEndpointCasesResults from "./TestEndpointCasesResults";
import { API_BACKEND_URL } from "../../../Config/Config";
import apiFetch from "../../../utils/apiFetch";
import {
  useAddVariablesTcEnd,
  useEditTestCase,
  useFetchLatestResults,
  useEditEndpoint,
} from "../../../useQuery";
import { useNotification } from "../../../hooks";
import BodyEditor from "./BodyEditor";
import HeadersEditor from "./HeadersEditor";
import QueryParamsEditor from "./QueryParamsEditor";
import PathParamsEditor from "./PathParamsEditor";
import { getEndpointLabel } from "../utils/endpointLabel";
import { applyPathParams, buildCurlCommand } from "../utils/toCurl";
import { copyToClipboard } from "../../../utils/copyToClipboard";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2Icon, Edit2Icon, Check, X, Link2, TerminalIcon } from "lucide-react";
import { useRequestData, normalizeBodyType } from "../../context/RequestDataContext";
import { Scripts } from "./Scripts";
import { useScriptExecutor } from "./Scripts/ScriptExecutor";
import {
  useAddEnvironmentVariableMutation,
  useDeleteEnvironmentVariableMutation,
} from "../../../useQuery/useEnvironment";
import { fileToBase64 } from "../../../utils/userDetailsUtils";
import {
  applyVariableDiff,
  applyEnvironmentDiff,
  applyRequestModifications,
} from "./Scripts/utils/scriptDiffUtils";
import SourceTypeGroup from "./SourceTypeGroup";
import { groupCollectionsBySourceType } from "../utils/groupCollectionsBySourceType";

const asObject = (value) => {
  if (value && typeof value === "object") return value;
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

function MainContent({
  addNameToSidebar,
  apiRequestData,
  tab = "collections",
  startGenerationStatusPolling,
}) {
  const queryClient = useQueryClient();
  const notify = useNotification();
  const {
    interpolate,
    environments,
    selectedEnvironmentId,
    environmentVariables,
    globalVariables,
    GLOBAL_OPTION,
    NONE_OPTION,
    getGlobalVariable,
    getEnvironmentVariable,
    getEnvironmentVariables,
  } = useVariables();

  const {
    method,
    setMethod,
    url,
    setUrl,
    headers,
    setHeaders,
    disabledHeaders,
    setDisabledHeaders,
    queryParams,
    setQueryParams,
    pathParams,
    setPathParams,
    body,
    setBody,
    bodyType,
    setBodyType,
    bodySubType,
    setBodySubType,
    selectedTestCase,
    selectedEndpoint,
    loadTestCaseData,
    loadEndpointData,
    getBodyContent,
    updateBodyContent,
    formFields,
    urlEncodedFields,
    preRequestScript,
    setPreRequestScript,
    postResponseScript,
    setPostResponseScript,
    requestContext,
    responseContext,
  } = useRequestData();

  const [apiName, setApiName] = useState("");
  const [response5, setResponse5] = useState(null);
  const [chainResults, setChainResult] = useState(null);
  const [activeTab, setActiveTab] = useState("headers");
  const [sidebarTab, setSidebarTab] = useState(tab);
  const [endpointVariables, setEndpointVariables] = useState({});

  const [testcase, setTestcase] = useState("");
  const [loader, setLoader] = useState(false);
  const [collections, setCollections] = useState([]);
  const groupedCollections = groupCollectionsBySourceType(collections);
  const [chains, setChains] = useState([]);
  const [saveButton, setSaveButton] = useState(false);
  const [currentCollection, setCurrentCollection] = useState("");
  const [jsonInput, setJsonInput] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [open, setOpen] = React.useState(false);
  const [mainTab, setMainTab] = useState("mainTab");
  const [endpoint, setEndPoint] = useState("");

  const [chainTest, setChainTest] = useState("testResults");
  const [collectionId, setCollectionId] = useState(null);
  const [endpointFormOpen, setEndpointFormOpen] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);
  const [endpointResults, setEndpointResults] = useState([]);
  const [collectionResults, setCollectionResults] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalEndpointData, setOriginalEndpointData] = useState(null);
  const [applyToTestCases, setApplyToTestCases] = useState(false);

  const [variable, setVariable] = useState({ key: "", value: "" });
  const [currentCollectionData, setCurrentCollectionData] = useState(null);
  const [testCaseModalOpen, setTestCaseModalOpen] = useState(false);

  const [selectedCollectionId, setSelectedCollectionId] = useState(null);
  const [selectedEndpointId, setSelectedEndpointId] = useState(null);
  const [currentBaseUrl, setCurrentBaseUrl] = useState("");
  const [chainCreationModalOpen, setChainCreationModalOpen] = useState(false);
  const [selectedChainStepKey, setSelectedChainStepKey] = useState(null);
  const [lastRunChain, setLastRunChain] = useState(null);

  const [sidebarWidths, setSidebarWidths] = useState({
    collections: 320,
    chains: 320,
    history: 320,
  });
  const isResizing = useRef(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(320);
  const resizeTab = useRef("collections");

  const startResizing = (e) => {
    isResizing.current = true;
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = sidebarWidths[sidebarTab] || 320;
    resizeTab.current = sidebarTab;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isResizing.current) return;
      const delta = e.clientX - resizeStartX.current;
      const newWidth = Math.min(450, Math.max(320, resizeStartWidth.current + delta));
      setSidebarWidths((prev) => ({
        ...prev,
        [resizeTab.current]: newWidth,
      }));
    };
    const onMouseUp = () => {
      if (!isResizing.current) return;
      isResizing.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const [scriptLogs, setScriptLogs] = useState([]);
  const [scriptResults, setScriptResults] = useState({
    preRequest: null,
    postResponse: null,
  });

  const { executeScript } = useScriptExecutor();

  const addVariableMutation = useAddVariablesTcEnd({
    onSuccess: (data) => {
      queryClient.invalidateQueries(["latestAPIResults"]);
      notify("Variable added successfully!", "success");
      setVariable({ key: "", value: "" });
    },
    onError: (error) => {
      notify(`Error adding variable: ${error.message}`, "error");
    },
  });

  const editTestCaseMutation = useEditTestCase({
    onSuccess: (data) => {
      notify("Test Case updated successfully!", "success");
      setIsEditMode(false);
      setOriginalEndpointData(null);
      refetch();
    },
    onError: (error) => {
      notify(`Error updating test case: ${error.message}`, "error");
    },
  });

  const editEndpointMutation = useEditEndpoint({
    onSuccess: (data) => {
      notify("Endpoint updated successfully!", "success");
      setIsEditMode(false);
      setOriginalEndpointData(null);
      refetch();
    },
    onError: (error) => {
      notify(`Error updating endpoint: ${error.message}`, "error");
    },
  });

  const addEnvironmentVariableMutation = useAddEnvironmentVariableMutation({
    onSuccess: () => {
      queryClient.invalidateQueries(["environmentVariables", selectedEnvironmentId]);
    },
    onError: (error) => {
      console.error("Failed to add environment variable:", error);
    },
  });

  const deleteEnvironmentVariableMutation = useDeleteEnvironmentVariableMutation({
    onSuccess: () => {
      queryClient.invalidateQueries(["environmentVariables", selectedEnvironmentId]);
    },
    onError: (error) => {
      console.error("Failed to delete environment variable:", error);
    },
  });

  const handleEditEndpoint = () => {
    if (!endpoint?.id) {
      notify("No endpoint selected to update", "error");
      return;
    }
    setOriginalEndpointData({
      headers: headers,
      disabledHeaders: disabledHeaders,
      body: body,
      queryParams: queryParams,
      pathParams: pathParams,
      url: url,
      method: method,
      bodyType: bodyType,
      bodySubType: bodySubType,
    });
    setIsEditMode(true);
  };

  const handleUrlChange = (newUrl) => {
    setUrl(newUrl);

    const matches = [...newUrl.matchAll(/\{([^}]+)\}|:([a-zA-Z0-9_]+)(?=\/|$)/g)];
    if (matches.length > 0) {
      let currentPathParams = {};
      try {
        currentPathParams =
          typeof pathParams === "string" ? JSON.parse(pathParams) : pathParams || {};
      } catch (e) {
        currentPathParams = {};
      }

      const newPathParams = { ...currentPathParams };
      let changed = false;

      matches.forEach((match) => {
        const paramName = match[1] || match[2];
        if (paramName && !newPathParams.hasOwnProperty(paramName)) {
          newPathParams[paramName] = "";
          changed = true;
        }
      });

      if (changed) {
        setPathParams(JSON.stringify(newPathParams, null, 2));
      }
    }
  };


  const editorCollection = collections.find((c) => c.id === collectionId);
  const editorBaseUrl = editorCollection?.baseurl || currentBaseUrl || "";
  const editorHasSplit = !!editorBaseUrl && url.startsWith(editorBaseUrl);
  const editorPathValue = editorHasSplit ? url.slice(editorBaseUrl.length) : url;

  const handlePathOnlyChange = (newPath) => {
    const isAbsoluteOverride = newPath.includes("http://") || newPath.includes("https://");
    handleUrlChange(isAbsoluteOverride ? newPath : editorBaseUrl + newPath);
  };

  const determineBodySubType = () => {
    if (bodyType === "form-data") {
      const hasFields = formFields.some((field) => field.key);
      if (!hasFields) return "text";

      const hasFileField = formFields.some((field) => field.key && field.type === "file");
      const hasTextField = formFields.some((field) => field.key && field.type === "text");

      if (hasFileField && hasTextField) {
        return "mixed";
      }

      if (hasFileField) {
        return "file";
      }

      return "text";
    }
    return bodySubType;
  };

  const handleSaveEndpoint = async () => {
    const safeJsonParse = (jsonString, fallback = {}) => {
      if (!jsonString || jsonString.trim() === "") return fallback;
      try {
        if (typeof jsonString === "object") return jsonString;
        return JSON.parse(jsonString);
      } catch (error) {
        console.warn("Failed to parse JSON:", error);
        return fallback;
      }
    };

    const getCurrentBodyContent = async () => {
      switch (bodyType) {
        case "form-data":
          const hasFormData = formFields.some((field) => field.key);
          if (hasFormData) {
            // Convert files to base64 for storage
            const formattedFields = await Promise.all(
              formFields
                .filter((field) => field.key)
                .map(async (field) => {
                  if (field.type === "file" && field.fileObject) {
                    const base64Data = await fileToBase64(field.fileObject);
                    return {
                      key: field.key,
                      value: field.value || null,
                      type: field.type,
                      description: field.description || null,
                      required: field.required || false,
                      src: field.src || null,
                      fileName: field.fileObject.name,
                      fileSize: field.fileObject.size,
                      fileType: field.fileObject.type,
                      fileData: base64Data,
                    };
                  }

                  return {
                    key: field.key,
                    value: field.value || null,
                    type: field.type,
                    description: field.description || null,
                    required: field.required || false,
                    src: field.type === "file" ? field.src || null : null,
                  };
                }),
            );

            return JSON.stringify({ fields: formattedFields });
          }
          return getBodyContent("form-data");

        case "form-urlencoded":
          const hasUrlData = urlEncodedFields.some((field) => field.key && field.value);
          if (hasUrlData) {
            return urlEncodedFields
              .filter((field) => field.key)
              .map((field) => `${encodeURIComponent(field.key)}=${encodeURIComponent(field.value)}`)
              .join("&");
          }
          return getBodyContent("form-urlencoded");

        case "binary":
          const binaryContent = getBodyContent("binary");
          if (binaryContent?.fileObject) {
            const base64Data = await fileToBase64(binaryContent.fileObject);
            return JSON.stringify({
              fileName: binaryContent.fileObject.name,
              fileSize: binaryContent.fileObject.size,
              fileType: binaryContent.fileObject.type,
              fileData: base64Data,
            });
          }
          return getBodyContent("binary");

        case "none":
          return "";

        default:
          return getBodyContent(bodyType);
      }
    };

    const currentBodyContent = await getCurrentBodyContent();
    const currentBodySubType = determineBodySubType();

    
    const activeCollection = collections.find((c) => c.id === collectionId);
    const authoritativeBaseUrl = activeCollection?.baseurl || currentBaseUrl || "";
    const resolvedPath =
      authoritativeBaseUrl && url.startsWith(authoritativeBaseUrl)
        ? url.slice(authoritativeBaseUrl.length) || "/"
        : url;

    if (selectedTestCase?.id) {
      const updatedTestCaseData = {
        collectionId: collectionId,
        testCaseId: selectedTestCase.id,
        endpointId: endpoint.id,
        url: resolvedPath,
        method: method,
        headers: headers,
        disabledParams: JSON.stringify({ headers: safeJsonParse(disabledHeaders, {}) }),
        body: currentBodyContent,
        bodyType: bodyType,
        bodySubType: currentBodySubType,
        queryParams: safeJsonParse(queryParams, {}),
        pathParams: safeJsonParse(pathParams, {}),
      };
      editTestCaseMutation.mutate({ data: updatedTestCaseData });
    } else if (endpoint?.id) {
      const updatedEndpointData = {
        endpointId: endpoint.id,
        method: method,
        path: resolvedPath,
        headers: headers,
        disabledParams: JSON.stringify({ headers: safeJsonParse(disabledHeaders, {}) }),
        body: currentBodyContent,
        bodyType: bodyType,
        bodySubType: currentBodySubType,
        queryParams: safeJsonParse(queryParams, {}),
        pathParams: safeJsonParse(pathParams, {}),
        forTestCase: applyToTestCases,
      };
      editEndpointMutation.mutate({ data: updatedEndpointData });
    } else {
      notify("No test case or endpoint selected to update", "error");
    }
  };

  const handleCancelEdit = () => {
    // Restore original data
    if (originalEndpointData) {
      setHeaders(originalEndpointData.headers);
      setDisabledHeaders(originalEndpointData.disabledHeaders || "{}");
      setBody(originalEndpointData.body);
      setQueryParams(originalEndpointData.queryParams);
      setPathParams(originalEndpointData.pathParams || "{}");
      setUrl(originalEndpointData.url);
      setMethod(originalEndpointData.method);
      setBodyType(originalEndpointData.bodyType || "json");
      setBodySubType(originalEndpointData.bodySubType || "json");
    }
    setIsEditMode(false);
    setOriginalEndpointData(null);
    setApplyToTestCases(false);
  };
  const userDetails = localStorage.getItem("userDetail");
  const user = JSON.parse(userDetails);
  const {
    data: latestResults,
    isError,
    isLoading: queryLoading,
    refetch,
  } = useFetchLatestResults({
    enabled: true,
  });

  useEffect(() => {
    setSidebarTab(tab);

    if (tab !== "chains") {
      setChainResult(null);
      setChainTest("testResults");
      setSelectedChainStepKey(null);
    }
  }, [tab]);

  const handleMainTabChange = (event) => {
    setMainTab(event.target.value);
  };
  const handleTabChange = (event, tab) => {
    setActiveTab(tab);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  useEffect(() => {
    fetchLatestResults();
  }, []);

  const handleOpenCollectionDialog = () => {
    setCollectionDialogOpen(true);
  };
  const handleCloseCollectionDialog = () => {
    setCollectionDialogOpen(false);
  };
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleEndpointFormClose = () => {
    setEndpointFormOpen(false);
  };

  // TODO(script-cleanup): this runs the same pre-request script the backend now also runs
  // server-side via NgRuntime as part of processApiRequest (POST /api/test never tells the
  // backend preRequestScript/postResponseScript, so it resolves and runs the endpoint/test
  // case's own stored script independently). Both sides can call ng.environment.set and persist
  // to the DB, so a single Send can double-write environment variables through two different JS
  // engines. Before removing this and switching to the backend's returned scriptResults/variables,
  // confirm no stored pre_request script relies on applyRequestModifications' direct mutation of
  // context.request.url/headers/body/queryParams - the backend's ng.request is read-only and has
  // no equivalent, so that capability would need to move server-side first.
  // execute pre-request script
  const executePreRequestScript = async (requestData) => {
    if (!preRequestScript.trim()) {
      return {
        requestData,
        updatedVariables: endpointVariables,
        updatedEnvironment: getEnvironmentVariables(),
      };
    }

    try {
      let localVariables = { ...endpointVariables };
      let modifiedRequestData = { ...requestData };
      const initialEnv = getEnvironmentVariables();

      const context = {
        url: requestData.url,
        method: requestData.method,
        headers: requestData.headers,
        body: requestData.body,
        queryParams: requestData.queryParams,
        pathParams: requestData.pathParams,
        variables: localVariables,
        environment: initialEnv,
        request: {
          url: requestData.url,
          method: requestData.method,
          headers: requestData.headers,
          body: requestData.body,
          queryParams: requestData.queryParams,
          pathParams: requestData.pathParams,
        },
      };

      const result = await executeScript(preRequestScript, context, "pre-request");

      setScriptResults((prev) => ({ ...prev, preRequest: result }));
      if (result.logs) {
        setScriptLogs((prev) => [
          ...prev,
          ...result.logs.map((log) => ({ ...log, type: "pre-request" })),
        ]);
      }

      if (result.success) {
        localVariables = applyVariableDiff(
          localVariables,
          result.variables,
          setEndpointVariables,
          endpoint?.id,
          selectedTestCase?.id,
          addVariableMutation,
        );

        applyEnvironmentDiff(
          initialEnv,
          result.environment,
          environmentVariables,
          selectedEnvironmentId,
          addEnvironmentVariableMutation,
          deleteEnvironmentVariableMutation,
          GLOBAL_OPTION,
          NONE_OPTION,
        );

        modifiedRequestData = applyRequestModifications(
          modifiedRequestData,
          result.modifiedContext,
        );
      }

      return {
        requestData: modifiedRequestData,
        updatedVariables: localVariables,
        updatedEnvironment: result.success && result.environment ? result.environment : initialEnv,
      };
    } catch (error) {
      console.error("[PRE-REQUEST] Script failed:", error);
      return {
        requestData,
        updatedVariables: endpointVariables,
        updatedEnvironment: getEnvironmentVariables(),
      };
    }
  };

  // TODO(script-cleanup): same duplicate-execution issue as executePreRequestScript above - the
  // backend already ran this post-response script server-side and returned its own results.
  // Execute post-response script
  const executePostResponseScript = async (responseData) => {
    if (!postResponseScript.trim()) {
      return;
    }

    try {
      const initialEnv = getEnvironmentVariables();

      const context = {
        url: url,
        method: method,
        headers: headers,
        body: body,
        queryParams: queryParams,
        variables: endpointVariables,
        environment: initialEnv,
        // status/timing/size/headers come back as real fields; falling through to a literal
        // 200 here would make ng.response.code meaningless.
        response: {
          status: responseData?.status ?? responseData?.originalResponse?.status ?? 0,
          statusText: responseData?.statusText || "",
          headers: responseData?.responseHeaders || responseData?.headers || {},
          responseTime: responseData?.responseTime ?? 0,
          responseSize: responseData?.responseSize ?? 0,
          body: responseData?.body || responseData?.originalResponse,
          originalResponse: responseData?.originalResponse,
          cookies: responseData?.cookies || [],
        },
      };

      const result = await executeScript(postResponseScript, context, "post-response");

      setScriptResults((prev) => ({ ...prev, postResponse: result }));
      if (result.logs) {
        setScriptLogs((prev) => [
          ...prev,
          ...result.logs.map((log) => ({ ...log, type: "post-response" })),
        ]);
      }

      if (result.success) {
        applyVariableDiff(
          endpointVariables,
          result.variables,
          setEndpointVariables,
          endpoint?.id,
          selectedTestCase?.id,
          addVariableMutation,
        );

        applyEnvironmentDiff(
          initialEnv,
          result.environment,
          environmentVariables,
          selectedEnvironmentId,
          addEnvironmentVariableMutation,
          deleteEnvironmentVariableMutation,
          GLOBAL_OPTION,
          NONE_OPTION,
        );
      }
    } catch (error) {
      console.error(" [POST-RESPONSE] Script failed:", error);
    }
  };

  const getCurrentBodyContent = async () => {
    switch (bodyType) {
      case "form-data":
        const hasFormData = formFields.some((field) => field.key);
        if (hasFormData) {
          // Convert files to base64
          const formattedFields = await Promise.all(
            formFields
              .filter((field) => field.key)
              .map(async (field) => {
                if (field.type === "file" && field.fileObject) {
                  const base64Data = await fileToBase64(field.fileObject);
                  return {
                    key: field.key,
                    value: field.value || null,
                    type: field.type,
                    description: field.description || null,
                    required: field.required || false,
                    src: field.src || null,
                    fileName: field.fileObject.name,
                    fileSize: field.fileObject.size,
                    fileType: field.fileObject.type,
                    fileData: base64Data, // ✅ Base64 encoded file
                  };
                } else if (field.type === "file" && !field.fileObject) {
                  // File metadata without actual file (e.g., from loaded endpoint/test case)
                  return {
                    key: field.key,
                    value: field.value || null,
                    type: field.type,
                    description: field.description || null,
                    required: field.required || false,
                    src: field.src || null,
                    fileName: field.fileName || field.value || null,
                    fileSize: field.fileSize || null,
                  };
                }

                // Text fields
                return {
                  key: field.key,
                  value: field.value || null,
                  type: field.type,
                  description: field.description || null,
                  required: field.required || false,
                  src: null,
                };
              }),
          );

          return JSON.stringify({ fields: formattedFields });
        }
        return getBodyContent("form-data");

      case "form-urlencoded":
        const hasUrlData = urlEncodedFields.some((field) => field.key && field.value);
        if (hasUrlData) {
          return urlEncodedFields
            .filter((field) => field.key)
            .map((field) => `${encodeURIComponent(field.key)}=${encodeURIComponent(field.value)}`)
            .join("&");
        }
        return getBodyContent("form-urlencoded");

      case "binary":
        const binaryContent = getBodyContent("binary");
        if (binaryContent?.fileObject) {
          const base64Data = await fileToBase64(binaryContent.fileObject);
          return JSON.stringify({
            fileName: binaryContent.fileObject.name,
            fileSize: binaryContent.fileObject.size,
            fileType: binaryContent.fileObject.type,
            fileData: base64Data, // ✅ Base64 encoded file
          });
        }

        // If it's already stored metadata (from loaded endpoint/test case)
        if (typeof binaryContent === "string" && binaryContent.startsWith("{")) {
          try {
            const parsed = JSON.parse(binaryContent);
            if (parsed.fileName) {
              return binaryContent;
            }
          } catch (e) {}
        }
        return getBodyContent("binary");

      case "none":
        return "";

      default:
        return getBodyContent(bodyType);
    }
  };

  const handleCopyAsCurl = async () => {
    try {
      const resolveValues = (value) =>
        Object.fromEntries(
          Object.entries(asObject(value)).map(([key, entry]) => [key, interpolate(entry)]),
        );

      const command = buildCurlCommand({
        method,
        url: interpolate(url || ""),
        headers: resolveValues(headers),
        queryParams: resolveValues(queryParams),
        pathParams: resolveValues(pathParams),
        body: interpolate(await getCurrentBodyContent()),
        bodyType,
      });

      await copyToClipboard(command);
      notify("cURL command copied to clipboard", "success");
    } catch (error) {
      console.error("Failed to build cURL command:", error);
      notify("Could not build the cURL command", "error");
    }
  };

  const handleSendRequest = async () => {
    setLoader(true);

    if (selectedTestCase?.id) {
      setActiveTab("testResults");
    } else {
      setActiveTab("responseBody");
    }

    setChainResult(null);
    const backendUrl = `${API_BACKEND_URL}/test?companyId=${user.companyid}`;

    try {
      const defaultStructure = {};

      const safeJsonParse = (jsonString, fallback = {}) => {
        if (!jsonString || jsonString.trim() === "") return fallback;

        try {
          if (typeof jsonString === "object") return jsonString;

          let parsed = JSON.parse(jsonString);

          if (typeof parsed === "string") {
            parsed = JSON.parse(parsed);
          }

          return parsed;
        } catch (error) {
          console.warn("Failed to parse JSON:", error);
          return fallback;
        }
      };

      // Parse jsonInput if it's a string
      const parsedJsonInput = jsonInput
        ? typeof jsonInput === "string"
          ? JSON.parse(jsonInput)
          : jsonInput
        : {};

      const currentBodyContent = await getCurrentBodyContent();

      // create initial request body (before interpolation)
      const initialRequestBody = {
        method: method || "",
        url: url || "",
        headers: headers,
        queryParams: queryParams,
        pathParams: pathParams,
        body: currentBodyContent,
        apiName: selectedTestCase?.testCase || getEndpointLabel(endpoint),
        id: selectedTestCase?.id || -1,
        expectedStatusCode: testcase.expectedStatusCode,
        requestKeyValuePairs: parsedJsonInput,
        endpointId: endpoint.id,
        bodyType: bodyType,
        bodySubType: determineBodySubType(),
        // ng.environment reads and writes this environment's variables
        environmentId:
          selectedEnvironmentId && typeof selectedEnvironmentId === "number"
            ? selectedEnvironmentId
            : null,
      };

      // Execute pre-request script FIRST (before interpolation)
      const {
        requestData: modifiedRequestBody,
        updatedVariables,
        updatedEnvironment,
      } = await executePreRequestScript(initialRequestBody);

      // NOW resolve all variables after pre-request script has run
      const tempResolver = createScopeResolver(
        asMap(globalVariables || []),
        updatedEnvironment || asMap(environmentVariables || []),
        updatedVariables,
      );

      const resolvedUrl = interpolateText(modifiedRequestBody.url, tempResolver);

      // handle headers - if it's already an object, interpolate each value
      let resolvedHeaders;
      if (typeof modifiedRequestBody.headers === "object" && modifiedRequestBody.headers !== null) {
        resolvedHeaders = {};
        Object.entries(modifiedRequestBody.headers).forEach(([key, value]) => {
          resolvedHeaders[key] = interpolateText(value, tempResolver);
        });
      } else {
        resolvedHeaders = interpolateText(modifiedRequestBody.headers, tempResolver);
      }

      const resolvedBody = interpolateText(modifiedRequestBody.body, tempResolver);

      // Handle query params - if it's already an object, interpolate each value
      let resolvedQueryParams;
      if (
        typeof modifiedRequestBody.queryParams === "object" &&
        modifiedRequestBody.queryParams !== null
      ) {
        resolvedQueryParams = {};
        Object.entries(modifiedRequestBody.queryParams).forEach(([key, value]) => {
          resolvedQueryParams[key] = interpolateText(value, tempResolver);
        });
      } else {
        resolvedQueryParams = interpolateText(modifiedRequestBody.queryParams, tempResolver);
      }

      const safeHeaders =
        typeof resolvedHeaders === "object"
          ? resolvedHeaders
          : safeJsonParse(resolvedHeaders, defaultStructure);
      const safeQueryParams =
        typeof resolvedQueryParams === "object"
          ? resolvedQueryParams
          : safeJsonParse(resolvedQueryParams, defaultStructure);

      let resolvedPathParams;
      if (
        typeof modifiedRequestBody.pathParams === "object" &&
        modifiedRequestBody.pathParams !== null
      ) {
        resolvedPathParams = {};
        Object.entries(modifiedRequestBody.pathParams).forEach(([key, value]) => {
          resolvedPathParams[key] = interpolateText(value, tempResolver);
        });
      } else {
        resolvedPathParams = interpolateText(modifiedRequestBody.pathParams, tempResolver);
      }

      const safePathParams =
        typeof resolvedPathParams === "object"
          ? resolvedPathParams
          : safeJsonParse(resolvedPathParams, defaultStructure);

      const safeBody = resolvedBody || "";

      const finalUrl = applyPathParams(resolvedUrl || "", safePathParams);

      // ✅ Always send as JSON (no FormData)
      const finalRequestBody = {
        method: modifiedRequestBody.method || "",
        url: finalUrl,
        headers: safeHeaders,
        queryParams: safeQueryParams,
        pathParams: safePathParams,
        body: safeBody, // Contains base64 encoded files if present
        apiName: modifiedRequestBody.apiName || "",
        id: modifiedRequestBody.id || -1,
        expectedStatusCode: modifiedRequestBody.expectedStatusCode,
        requestKeyValuePairs: modifiedRequestBody.requestKeyValuePairs,
        endpointId: modifiedRequestBody.endpointId,
        bodyType: modifiedRequestBody.bodyType,
        bodySubType: modifiedRequestBody.bodySubType,
        environmentId: modifiedRequestBody.environmentId ?? null,
      };

      const response = await apiFetch(backendUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(finalRequestBody),
      });

      const data = await response?.json();

      if (data.error || data.message) {
        setResponse5({
          originalResponse: {
            status: response.status,
            message: data.error || data.message,
            details: data,
          },
        });
        return;
      }

      setResponse5(data);

      // Execute post-response script
      await executePostResponseScript(data);
    } catch (err) {
      console.error("Error details:", {
        message: err.message,
        stack: err.stack,
      });

      setResponse5({
        originalResponse: {
          status: err.status || 500,
          message: err.message || "An unexpected error occurred",
          details: err,
        },
      });
    } finally {
      setLoader(false);
      setChainTest("testResults");
    }
  };

  /**
   * Loads a chain step into the request panel.
   *
   * A step is its own record — it carries the body, headers, scripts and output mapping that
   * will actually be sent, which are usually not the endpoint's. The matched endpoint is used
   * only to resolve the base URL and to highlight the tree; everything shown is the step's.
   */
  const handleChainStepClick = (step, chain) => {
    setSelectedChainStepKey(`${chain.id}-${step.step_number}`);
    setResponse5("");
    setChainResult(null);

    const stepPath = step.endpoint;
    const stepMethod = step.http_method;

    let matchedEndpoint = null;
    let matchedCollection = null;
    for (const collection of collections) {
      const found = collection.endpoints?.find((ep) => {
        const epPath = ep.path?.split("?")[0];
        return (
          ep.method === stepMethod &&
          (epPath === stepPath || epPath?.endsWith(stepPath) || stepPath?.endsWith(epPath))
        );
      });
      if (found) {
        matchedEndpoint = found;
        matchedCollection = collection;
        break;
      }
    }

    if (matchedEndpoint && matchedCollection) {
      setEndPoint(matchedEndpoint);
      setCollectionId(matchedCollection.id);
      setSelectedEndpointId(matchedEndpoint.id);
      setSelectedCollectionId(matchedCollection.id);
    }

    const baseUrl = matchedCollection?.baseurl || chain?.base_url || currentBaseUrl || "";
    setCurrentBaseUrl(baseUrl);
    const isAbsolute = stepPath?.includes("http");
    setUrl(isAbsolute ? stepPath : `${baseUrl}${stepPath}`);
    setMethod(stepMethod);

    // The step's own request, not the endpoint's.
    const stepHeaders = step.headers && Object.keys(step.headers).length > 0 ? step.headers : null;
    setHeaders(stepHeaders ? JSON.stringify(stepHeaders, null, 2) : "{}");
    setDisabledHeaders("{}");

    const stepBody = step.input_parameters;
    const hasBody = stepBody && Object.keys(stepBody).length > 0;
    const bodyText = hasBody ? JSON.stringify(stepBody, null, 2) : "";
    const contentType = stepHeaders
      ? Object.entries(stepHeaders).find(([k]) => k.toLowerCase() === "content-type")?.[1]
      : null;
    const resolvedBodyType = normalizeBodyType(
      contentType && String(contentType).includes("x-www-form-urlencoded")
        ? "x-www-form-urlencoded"
        : "json",
    );

    setBody(bodyText);
    setBodyType(resolvedBodyType);
    setBodySubType(resolvedBodyType);
    updateBodyContent(resolvedBodyType, bodyText);

    setQueryParams("{}");
    setPathParams("{}");

    // Scripts the step will actually run.
    setPreRequestScript(step.pre_request || "");
    setPostResponseScript(step.post_response || "");
    setScriptLogs([]);
    setScriptResults({ preRequest: null, postResponse: null });

    // What this step publishes into the chain scope, shown under Environment Variables.
    setEndpointVariables(step.output_mapping || {});

    setActiveTab(stepMethod === "GET" || stepMethod === "DELETE" ? "queryParams" : "body");
  };

  useEffect(() => {
    if (latestResults) {
      try {
        if (!currentBaseUrl && latestResults?.collections[0]?.baseurl) {
          setCurrentBaseUrl(latestResults.collections[0].baseurl);
        }
        setCollections(latestResults.collections || []);
        setChains(latestResults.chains || []);
      } catch (error) {
        console.error("Error parsing response:", error);
        setCollections([]);
      }
    }
  }, [latestResults]);

  useEffect(() => {
    if (endpoint?.variables) {
      setEndpointVariables(endpoint.variables);
    } else {
      setEndpointVariables({});
    }

    // reset script execution state when switching endpoints (scripts themselves are reset by RequestDataContext)

    setScriptLogs([]);
    setScriptResults({ preRequest: null, postResponse: null });
    setIsEditMode(false);
    setOriginalEndpointData(null);
    setApplyToTestCases(false);
  }, [endpoint]);

  const onTestCaseSelect = (testCase) => {
    if (!testCase) {
      // clear only test case related state, but keeep the endpoint
      loadTestCaseData(null);
      setTestcase("");
      setResponse5("");

      // Reset script state when clearing test case
      setScriptLogs([]);
      setScriptResults({ preRequest: null, postResponse: null });

      // restore endpoint data.
      if (endpoint?.id) {
        loadEndpointData(endpoint);

        const [pathWithoutQuery, queryString] = endpoint.path.split("?");
        const isCompleteEndpoint =
          pathWithoutQuery.includes("http") || pathWithoutQuery.startsWith("{{");
        const fullUrl = isCompleteEndpoint ? endpoint.path : `${currentBaseUrl}${endpoint.path}`;
        setUrl(fullUrl);

        let queryParamsObj = {};
        if (queryString) {
          queryParamsObj = queryString.split("&").reduce((acc, param) => {
            const [key, value] = param.split("=").map(decodeURIComponent);
            if (key) acc[key] = value || "";
            return acc;
          }, {});
        }

        if (endpoint.queryParams) {
          try {
            const endpointParams =
              typeof endpoint.queryParams === "string"
                ? JSON.parse(endpoint.queryParams)
                : endpoint.queryParams;
            queryParamsObj = { ...queryParamsObj, ...endpointParams };
          } catch (error) {
            console.warn("Failed to parse endpoint.queryParams:", error);
          }
        }

        setQueryParams(
          Object.keys(queryParamsObj).length > 0 ? JSON.stringify(queryParamsObj, null, 2) : "{}",
        );
      }

      return;
    }

    if (testCase.description === "Add Testcase") {
      loadTestCaseData(null);
      setTestcase("");
      setResponse5("");
      setScriptLogs([]);
      setScriptResults({ preRequest: null, postResponse: null });
      handleOpenDialog();
      return;
    }

    try {
      loadTestCaseData(testCase);

      const updatedUrl = ConstructUrl(currentBaseUrl, testCase);
      setUrl(updatedUrl.fullUrl || "");

      setTestcase(testCase);
      setResponse5("");

      // Reset script state when selecting new test case
      setScriptLogs([]);
      setScriptResults({ preRequest: null, postResponse: null });
    } catch (error) {
      console.error("Error parsing test case data:", error);
      loadTestCaseData(null);
      setTestcase("");
      setResponse5("");
      setScriptLogs([]);
      setScriptResults({ preRequest: null, postResponse: null });
    }
  };

  const handleAddApiClick = () => {
    // setSaveButton(false);
    // const updatedCollections = _.cloneDeep(collections);
    // const curCollection = updatedCollections.map((collection) => {
    //   if (collection.name === currentCollection) {
    //     const newEndpoint = {
    //       path: url,
    //       method: method,
    //       summary: "",
    //       operationId: "",
    //       headers: { headers },
    //       body: { body },
    //     };
    //     collection.endpoints.push(newEndpoint);
    //   }
    // });
    // console.log("update", updatedCollections);
    // setCollections(updatedCollections);
  };

  // Function to replace path parameters with actual values
  const replacePathParams = (path, paramValue) => {
    return path.replace(/{(\w+)}/g, paramValue);
  };

  const fetchLatestResults = async (event) => {
    setActiveTab("headers");
    setLoader(true);

    try {
      await refetch();
      setLoader(false);
    } catch (error) {
      console.error("Error fetching results:", error);
      setLoader(false);
    }
  };

  const handleAddVariable = () => {
    if (!variable.key.trim() || !variable.value.trim()) {
      return;
    }

    const updatedVariables = {
      ...endpointVariables,
      [variable.key]: variable.value,
    };

    setEndpointVariables(updatedVariables);

    addVariableMutation.mutate({
      endpointId: endpoint?.id,
      testCaseId: selectedTestCase?.id,
      variables: updatedVariables,
    });
  };

  const handleDeleteVariable = (keyToDelete) => {
    const updatedVariables = { ...endpointVariables };
    delete updatedVariables[keyToDelete];

    setEndpointVariables(updatedVariables);

    addVariableMutation.mutate({
      endpointId: endpoint?.id,
      testCaseId: selectedTestCase?.id,
      variables: updatedVariables,
    });
  };

  return (
    <Box sx={{ width: "100%", height: "100vh", overflow: "hidden" }} className="">
      <Grid container sx={{ height: "100%", overflow: "hidden" }} className="">
        <Grid size={12} sx={{ height: "100%" }}>
          <div className="main-content h-[100%] w-full ">
            <div>
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  alignItems: "center",
                }}
              >
                <Snackbar
                  open={open}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  autoHideDuration={5000}
                  onClose={handleClose}
                  message={`${selectedFile ? selectedFile.name : "File"} uploaded successfully`}
                />
              </Stack>
            </div>

            <div className="h-[100%] w-full ">
              <TabContext value={mainTab} sx={{ height: "100%" }}>
                <TopNav activeTab={mainTab} onTabChange={handleMainTabChange} />
                <TabPanel sx={{ padding: 0, height: "calc(100vh - 120px)" }} value="mainTab">
                  <Grid container className="px-2 rounded-md bg-postman-gray-light h-full">
                    {/* Left Sidebar */}

                    <Grid item className="min-h-full border-r border-[#e0e0e0]">
                      <Box
                        className="h-full w-full overflow-hidden"
                        sx={{
                          width: sidebarWidths[sidebarTab] || 320,
                          minWidth: sidebarWidths[sidebarTab] || 320,
                          maxWidth: sidebarWidths[sidebarTab] || 320,
                          flexShrink: 0,
                        }}
                      >
                        <div
                          className={`h-full overflow-hidden ${sidebarTab === "chains" ? "" : "px-4 mt-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 hover:scrollbar-thumb-gray-500"}`}
                        >
                          {sidebarTab === "collections" && (
                            <div className="h-[calc(100vh-120px)] overflow-hidden">
                              <div className="h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 hover:scrollbar-thumb-gray-500">
                                <div className="flex justify-center mb-4">
                                  <button
                                    // onClick={handleOpenDialog}
                                    onClick={handleOpenCollectionDialog}
                                    className="w-full py-2 px-4 border border-gray-300 bg-white text-sm text-postman-gray-medium hover:bg-gray-100 flex items-center justify-center space-x-2 rounded-md font-apple cursor-pointer"
                                  >
                                    <PlusCircleIcon className="h-5 w-5" />
                                    <span>Add New Collection</span>
                                  </button>
                                </div>

                                {queryLoading ? (
                                  <div className="flex justify-center py-8">
                                    <Circles
                                      height="40"
                                      width="40"
                                      color="#1976d2"
                                      ariaLabel="loading-indicator"
                                    />
                                  </div>
                                ) : (
                                  groupedCollections.map(
                                    ({ sourceType, collections: groupCols }) => (
                                      <SourceTypeGroup
                                        key={sourceType}
                                        sourceType={sourceType}
                                        collections={groupCols}
                                        setUrl={setUrl}
                                        setMethod={setMethod}
                                        setHeaders={setHeaders}
                                        setQueryParams={setQueryParams}
                                        setSaveButton={setSaveButton}
                                        setCurrentCollection={setCurrentCollection}
                                        setEndPoint={setEndPoint}
                                        setActiveTab={setActiveTab}
                                        setCollectionId={setCollectionId}
                                        setCurrentCollectionData={setCurrentCollectionData}
                                        setEndpointFormOpen={setEndpointFormOpen}
                                        setResponse5={setResponse5}
                                        setChainResult={setChainResult}
                                        selectedCollectionId={selectedCollectionId}
                                        selectedEndpointId={selectedEndpointId}
                                        setSelectedCollectionId={setSelectedCollectionId}
                                        setSelectedEndpointId={setSelectedEndpointId}
                                        setBaseUrl={setCurrentBaseUrl}
                                        loadEndpointData={loadEndpointData}
                                        startGenerationStatusPolling={startGenerationStatusPolling}
                                        setCollectionResults={setCollectionResults}
                                        setChainTest={setChainTest}
                                        setLoader={setLoader}
                                      />
                                    ),
                                  )
                                )}

                                <CollectionForm
                                  // open={openDialog}
                                  open={collectionDialogOpen}
                                  // handleCloseDialog={handleCloseDialog}
                                  handleCloseDialog={handleCloseCollectionDialog}
                                />

                                {endpointFormOpen && (
                                  <EndpointForm
                                    collectionID={collectionId}
                                    onClose={handleEndpointFormClose}
                                    collection={currentCollectionData}
                                  />
                                )}
                              </div>
                            </div>
                          )}
                          {sidebarTab === "chains" && (
                            <div className="h-[calc(100vh-120px)] flex flex-col overflow-hidden">
                              {/* Section header */}
                              <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 flex-shrink-0">
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                  Chains
                                  {chains?.length > 0 && (
                                    <span className="ml-2 text-xs font-normal text-gray-400 normal-case tracking-normal">
                                      {chains.length}
                                    </span>
                                  )}
                                </span>
                                <Tooltip title="Create new chain" arrow placement="right">
                                  <button
                                    onClick={() => setChainCreationModalOpen(true)}
                                    className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer text-gray-500 hover:text-gray-700"
                                  >
                                    <PlusCircleIcon className="h-5 w-5" />
                                  </button>
                                </Tooltip>
                              </div>

                              {/* Chain list */}
                              <div className="flex-1 overflow-y-auto py-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                                {chains && chains.length > 0 ? (
                                  chains.map((chain, index) => (
                                    <Chain
                                      key={index}
                                      chain={chain}
                                      collections={collections}
                                      setLoader={setLoader}
                                      setActiveTab={setActiveTab}
                                      setResponse5={setResponse5}
                                      setChainResult={setChainResult}
                                      setChainTest={setChainTest}
                                      setEndPoint={setEndPoint}
                                      setUrl={setUrl}
                                      setMethod={setMethod}
                                      setBody={setBody}
                                      setHeaders={setHeaders}
                                      onStepClick={handleChainStepClick}
                                      onRunChain={(c) => setLastRunChain(c)}
                                      selectedStepKey={selectedChainStepKey}
                                    />
                                  ))
                                ) : (
                                  <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                                    <Link2 size={28} className="mb-2 opacity-40" />
                                    <p className="text-[11px] font-medium text-gray-500">
                                      No chains yet
                                    </p>
                                    <p className="text-[10px] text-gray-400 mt-1">
                                      Click + to create one
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {sidebarTab === "history" && (
                            <div className="h-[calc(100vh-120px)] overflow-hidden">
                              <HistoryPanel
                                setUrl={setUrl}
                                setMethod={setMethod}
                                setResponse5={setResponse5}
                                setActiveTab={setActiveTab}
                              />
                            </div>
                          )}
                        </div>
                      </Box>
                    </Grid>

                    {/* ── Resize handle ──────────────────────────────────────── */}
                    <div
                      onMouseDown={startResizing}
                      style={{
                        width: "5px",
                        cursor: "col-resize",
                        flexShrink: 0,
                        backgroundColor: "transparent",
                        position: "relative",
                        zIndex: 10,
                        transition: "background-color 0.15s ease",
                      }}
                      className="group/resizer hover:bg-blue-400 active:bg-blue-500"
                      title="Drag to resize"
                    >
                      {/* Visual indicator line */}
                      <div className="absolute inset-y-0 left-[2px] w-px bg-gray-200 group-hover/resizer:bg-blue-400 transition-colors" />
                    </div>

                    {/* Main Request Panel */}
                    <div className="flex-1 flex flex-col h-[calc(100vh-100px)] overflow-hidden">
                      {sidebarTab !== "history" && sidebarTab !== "chains" && (
                        <div className="bg-white px-6 py-4 border-b border-gray-200">
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-4">
                              <button
                                style={{ justifyContent: "center" }}
                                onClick={() => setTestCaseModalOpen(true)}
                                className="flex items-center justify-between gap-2 px-4 py-2 text-gray-700 border border-gray-300 hover:border-gray-400 rounded-md transition-all font-apple text-sm min-w-[300px] group relative cursor-pointer"
                              >
                                View Test Cases
                              </button>
                              <button
                                onClick={handleOpenDialog}
                                disabled={!endpoint?.id}
                                title={
                                  endpoint?.id
                                    ? undefined
                                    : "Select an endpoint before adding a test case"
                                }
                                className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium font-apple text-sm transition-colors disabled:text-gray-400 disabled:hover:text-gray-400 disabled:cursor-not-allowed"
                              >
                                + Create New Test Case
                              </button>
                            </div>

                            {selectedTestCase?.id && selectedEndpointId && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-purple-500 font-medium font-apple whitespace-nowrap">
                                  Test Case:
                                </span>
                                <span className="text-xs text-purple-800 font-apple font-semibold">
                                  {selectedTestCase.testCase}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Request URL Bar */}
                      <div className="bg-white px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center border border-gray-300 bg-white overflow-hidden">
                          <div className="flex items-center">
                            <FormControl variant="outlined" size="small" sx={{ minWidth: 100 }}>
                              <Select
                                value={method}
                                onChange={(e) => setMethod(e.target.value)}
                                className="font-apple"
                                sx={{
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "& .MuiSelect-select": {
                                    padding: "12px 16px",
                                    fontSize: "12px",
                                    fontWeight: 400,
                                    color:
                                      method === "GET"
                                        ? "#16a34a"
                                        : method === "POST"
                                          ? "#ea580c"
                                          : method === "PUT"
                                            ? "#2563eb"
                                            : method === "PATCH"
                                              ? "#9333ea"
                                              : "#dc2626",
                                    backgroundColor: "white",
                                    "&:focus": {
                                      backgroundColor: "white",
                                    },
                                  },
                                  "& .MuiSelect-icon": {
                                    color:
                                      method === "GET"
                                        ? "#16a34a"
                                        : method === "POST"
                                          ? "#ea580c"
                                          : method === "PUT"
                                            ? "#2563eb"
                                            : method === "PATCH"
                                              ? "#9333ea"
                                              : "#dc2626",
                                  },
                                }}
                              >
                                <MenuItem
                                  value="GET"
                                  sx={{ color: "#16a34a", fontWeight: 400 }}
                                  className="font-apple"
                                >
                                  GET
                                </MenuItem>
                                <MenuItem
                                  value="POST"
                                  sx={{ color: "#ea580c", fontWeight: 400 }}
                                  className="font-apple"
                                >
                                  POST
                                </MenuItem>
                                <MenuItem
                                  value="PUT"
                                  sx={{ color: "#2563eb", fontWeight: 400 }}
                                  className="font-apple"
                                >
                                  PUT
                                </MenuItem>
                                <MenuItem
                                  value="PATCH"
                                  sx={{ color: "#9333ea", fontWeight: 400 }}
                                  className="font-apple"
                                >
                                  PATCH
                                </MenuItem>
                                <MenuItem
                                  value="DELETE"
                                  sx={{ color: "#dc2626", fontWeight: 400 }}
                                  className="font-apple"
                                >
                                  DELETE
                                </MenuItem>
                              </Select>
                            </FormControl>
                            <div className="h-8 w-px bg-gray-300"></div>
                          </div>

                          {editorHasSplit && (
                            <div
                              className="flex-shrink-0 pl-4 py-3 font-mono text-sm text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis max-w-[240px]"
                              title={editorBaseUrl}
                            >
                              {editorBaseUrl}
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <VariableInput
                              value={editorHasSplit ? editorPathValue : url}
                              onChange={editorHasSplit ? handlePathOnlyChange : handleUrlChange}
                              placeholder="Enter request URL"
                              className="flex-1 px-4 py-3 font-mono text-sm border-0 focus:outline-none focus:ring-0 bg-white w-full"
                            />
                          </div>

                          <TestCaseForm
                            open={openDialog}
                            endpointId={endpoint.id}
                            handleCloseDialog={handleCloseDialog}
                            collections={collections}
                          />

                          <div className="border-l border-gray-300 pl-4 pr-4 flex-shrink-0">
                            {isEditMode ? (
                              <div className="flex items-center space-x-3">
                                {/* Toggle for applying changes to test cases - only show for endpoint edit */}
                                {!selectedTestCase?.id && (
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      id="applyToTestCases"
                                      checked={applyToTestCases}
                                      onChange={(e) => setApplyToTestCases(e.target.checked)}
                                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                    />
                                    <label
                                      htmlFor="applyToTestCases"
                                      className="text-xs text-gray-600 font-apple cursor-pointer whitespace-nowrap"
                                    >
                                      Apply to Test Cases
                                    </label>
                                  </div>
                                )}

                                <div className="flex space-x-2">
                                  <button
                                    onClick={handleCancelEdit}
                                    className="px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium font-apple text-xs transition-colors cursor-pointer flex items-center gap-1"
                                    disabled={
                                      editEndpointMutation.isPending ||
                                      editTestCaseMutation.isPending
                                    }
                                  >
                                    <X size={12} />
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleSaveEndpoint}
                                    className={`px-3 py-2 ${selectedTestCase?.id ? "bg-purple-600 hover:bg-purple-700" : "bg-green-600 hover:bg-green-700"} text-white font-medium font-apple text-xs transition-colors cursor-pointer flex items-center gap-1`}
                                    disabled={
                                      editEndpointMutation.isPending ||
                                      editTestCaseMutation.isPending
                                    }
                                  >
                                    <Check size={12} />
                                    {editEndpointMutation.isPending ||
                                    editTestCaseMutation.isPending
                                      ? "Saving..."
                                      : "Save"}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex space-x-2">
                                {endpoint?.id && (
                                  <button
                                    onClick={handleEditEndpoint}
                                    className={`px-3 py-2 ${selectedTestCase?.id ? "bg-purple-600 hover:bg-purple-700" : "bg-orange-600 hover:bg-orange-700"} text-white font-medium font-apple text-xs transition-colors cursor-pointer flex items-center gap-1`}
                                  >
                                    <Edit2Icon size={12} />
                                    {selectedTestCase?.id ? "Edit Testcase" : "Edit Endpoint"}
                                  </button>
                                )}
                                <button
                                  onClick={handleCopyAsCurl}
                                  title="Copy this request as a cURL command, with variables resolved"
                                  className="px-3 py-2 bg-gray-700 hover:bg-gray-800 text-white font-medium font-apple text-xs transition-colors cursor-pointer flex items-center gap-1"
                                >
                                  <TerminalIcon size={12} />
                                  cURL
                                </button>
                                {sidebarTab !== "history" &&
                                  (saveButton ? (
                                    <button
                                      onClick={handleAddApiClick}
                                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium font-apple text-xs transition-colors cursor-pointer"
                                    >
                                      Save
                                    </button>
                                  ) : (
                                    <button
                                      onClick={handleSendRequest}
                                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium font-apple text-xs transition-colors cursor-pointer"
                                    >
                                      Test
                                    </button>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Tabs Section */}
                      <div className="flex-1 flex flex-col bg-white overflow-y-auto ">
                        {/* Tab Navigation */}
                        <div className="border-b border-gray-200 bg-gray-50">
                          <div className="flex">
                            {[
                              { label: "Path Params", value: "pathParams" },
                              { label: "Query Params", value: "queryParams" },
                              { label: "Headers", value: "headers" },
                              { label: "Body", value: "body" },
                              { label: "Test Scripts", value: "testScripts" },
                              { label: "Response", value: "responseBody" },
                              { label: "Cookies", value: "cookies" },
                              { label: "Test Results", value: "testResults" },
                              { label: "Environment Variables", value: "envVariables" },
                            ].map((tab, index) => (
                              <button
                                key={tab.value}
                                onClick={() => handleTabChange(null, tab.value)}
                                className={`px-6 py-3 text-sm font-medium font-apple border-r border-gray-200 transition-colors cursor-pointer ${
                                  activeTab === tab.value
                                    ? "bg-blue-600 text-white border-b-2 border-blue-600"
                                    : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                                } ${index === 0 ? "border-l-0" : ""}`}
                              >
                                {tab.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Tab Content */}
                        <div className="flex-1 overflow-hidden scrollbar-thin">
                          <div className="h-full p-6">
                            {activeTab === "pathParams" && (
                              <PathParamsEditor value={pathParams} onChange={setPathParams} />
                            )}

                            {activeTab === "queryParams" && (
                              <QueryParamsEditor
                                value={queryParams}
                                onChange={setQueryParams}
                                onUrlParamsChange={(queryString) => {
                                  const baseUrl = url.split("?")[0];
                                  setUrl(queryString ? `${baseUrl}?${queryString}` : baseUrl);
                                }}
                              />
                            )}

                            {activeTab === "headers" && (
                              <HeadersEditor
                                value={headers}
                                onChange={setHeaders}
                                disabledValue={disabledHeaders}
                                onDisabledChange={setDisabledHeaders}
                              />
                            )}

                            {activeTab === "body" && (
                              <BodyEditor
                                className="h-full"
                                onBodyTypeChange={(newBodyType, newBodySubType) => {
                                  setBodyType(newBodyType);
                                  setBodySubType(newBodySubType);
                                }}
                              />
                            )}

                            {activeTab === "testScripts" && (
                              <Scripts
                                endpointId={selectedEndpointId}
                                testCaseId={selectedTestCase?.id}
                                requestContext={{
                                  url: url,
                                  method: method,
                                  headers: headers,
                                  body: body,
                                  queryParams: queryParams,
                                  variables: endpointVariables,
                                  environment: getEnvironmentVariables(),
                                }}
                                responseContext={response5}
                                onScriptResult={(type, result) => {
                                  console.log(`${type} script result:`, result);

                                  if (result.success && result.variables) {
                                    Object.entries(result.variables).forEach(([key, value]) => {
                                      setEndpointVariables((prev) => ({ ...prev, [key]: value }));
                                    });
                                  }
                                }}
                                preRequestScript={preRequestScript}
                                postResponseScript={postResponseScript}
                                onPreRequestScriptChange={setPreRequestScript}
                                onPostResponseScriptChange={setPostResponseScript}
                                scriptLogs={scriptLogs}
                                scriptResults={scriptResults}
                                onClearLogs={() => {
                                  setScriptLogs([]);
                                  setScriptResults({ preRequest: null, postResponse: null });
                                }}
                                endpointData={endpoint}
                                testCaseData={selectedTestCase}
                              />
                            )}

                            {activeTab === "responseBody" && (
                              <div className="h-full overflow-auto">
                                <ResponseRenderer
                                  response={response5}
                                  isError={response5?.originalResponse?.status === "error"}
                                  className="h-full"
                                />
                              </div>
                            )}

                            {activeTab === "cookies" && (
                              <div className="h-full overflow-auto">
                                <CookiesPanel cookies={response5?.cookies} />
                              </div>
                            )}

                            {activeTab === "testResults" && (
                              <div className="h-full overflow-auto">
                                {chainTest === "chainResults" && (
                                  <ChainTestResults
                                    results={chainResults}
                                    chainSteps={
                                      lastRunChain ? JSON.parse(lastRunChain.steps || "[]") : []
                                    }
                                  />
                                )}
                                {chainTest === "testResults" && (
                                  <TestResults
                                    testResults={response5}
                                    scriptResults={scriptResults}
                                    hasPostResponseScript={Boolean(postResponseScript?.trim())}
                                    hasPreRequestScript={Boolean(preRequestScript?.trim())}
                                  />
                                )}
                                {chainTest === "endpointResults" && (
                                  <TestEndpointCasesResults results={endpointResults} />
                                )}
                                {chainTest === "collectionResults" && (
                                  <CollectionResults results={collectionResults} />
                                )}
                              </div>
                            )}
                            {activeTab === "envVariables" && (
                              <div className="h-full flex flex-col">
                                {/* shw Existing Variables*/}
                                {Object.keys(endpointVariables).length > 0 && (
                                  <div className="mb-6 p-4 border border-gray-200 bg-white rounded">
                                    <h3 className="text-base font-medium text-gray-900 mb-4">
                                      Current Variables
                                    </h3>
                                    <div className="space-y-2">
                                      {Object.entries(endpointVariables).map(
                                        ([key, value], index) => (
                                          <div
                                            key={index}
                                            className="flex items-center justify-between p-3 bg-gray-50 rounded border"
                                          >
                                            <div className="flex-1 grid grid-cols-2 gap-4">
                                              <div>
                                                <span className="text-xs text-gray-500 uppercase tracking-wide">
                                                  Key
                                                </span>
                                                <div className="font-mono text-sm text-gray-900">
                                                  {key}
                                                </div>
                                              </div>
                                              <div>
                                                <span className="text-xs text-gray-500 uppercase tracking-wide">
                                                  Value
                                                </span>
                                                <div className="font-mono text-sm text-gray-900">
                                                  {value}
                                                </div>
                                              </div>
                                            </div>
                                            <button
                                              onClick={() => handleDeleteVariable(key)}
                                              className="ml-4 px-3 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors cursor-pointer"
                                            >
                                              <Trash2Icon className="text-red-500" />
                                            </button>
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* New variables FOrm*/}
                                <div className="mb-4 p-4 border border-gray-200 bg-gray-50 rounded">
                                  <h3 className="text-base font-medium text-gray-900 mb-4">
                                    Add New Variable
                                  </h3>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div>
                                      <label className="block text-sm text-gray-600 mb-2">
                                        Key
                                      </label>
                                      <input
                                        type="text"
                                        value={variable.key}
                                        onChange={(e) =>
                                          setVariable({ ...variable, key: e.target.value })
                                        }
                                        placeholder="VARIABLE_NAME"
                                        className="w-full px-3 py-2 text-sm border border-gray-200 font-mono focus:outline-none focus:border-blue-400 cursor-text rounded"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm text-gray-600 mb-2">
                                        Value
                                      </label>
                                      <input
                                        type="text"
                                        value={variable.value}
                                        onChange={(e) =>
                                          setVariable({ ...variable, value: e.target.value })
                                        }
                                        placeholder="Variable value"
                                        className="w-full px-3 py-2 text-sm border border-gray-200 font-mono focus:outline-none focus:border-blue-400 cursor-text rounded"
                                      />
                                    </div>
                                  </div>

                                  <div className="flex items-center justify-between">
                                    <div className="flex space-x-3">
                                      <button
                                        onClick={() => {
                                          setVariable({ key: "", value: "" });
                                        }}
                                        className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer border border-gray-200 rounded hover:bg-gray-50"
                                        disabled={addVariableMutation.isPending}
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={handleAddVariable}
                                        className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer rounded disabled:opacity-50"
                                        disabled={addVariableMutation.isPending}
                                      >
                                        {addVariableMutation.isPending
                                          ? "Adding..."
                                          : "Add Variable"}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Grid>
                </TabPanel>
              </TabContext>
            </div>

            {loader && (
              <div className="loader-overlay">
                <Circles height="80" width="80" color="#1976d2" ariaLabel="loading-indicator" />
              </div>
            )}

            {/* Test Case Modal */}
            <TestCaseModal
              open={testCaseModalOpen}
              onClose={() => setTestCaseModalOpen(false)}
              endpoint={endpoint}
              baseUrl={currentBaseUrl}
              selectedTestCase={selectedTestCase}
              onTestCaseSelect={(testcase) => {
                onTestCaseSelect(testcase);
                setTestCaseModalOpen(false);
              }}
              setEndpointResults={setEndpointResults}
              setChainTest={setChainTest}
              setActiveTab={setActiveTab}
            />

            {/* Chain Creation Modal */}
            <ChainCreationModal
              open={chainCreationModalOpen}
              onClose={() => setChainCreationModalOpen(false)}
              onChainCreated={() => {
                // Refresh the chains data
                refetch();
              }}
            />
          </div>
        </Grid>
      </Grid>
    </Box>
  );
}

export default MainContent;
